import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Runs Envoy's sign-in handshake in a popup instead of a full-page redirect, so
 * the app tab stays mounted. Envoy's `/auth/signin/popup` endpoint drives the
 * OIDC handshake inside the popup, sets the same-origin session cookie, and
 * broadcasts on the `gs-auth` BroadcastChannel once it's done. The opener listens
 * on that channel and refetches the shared `['auth', 'userinfo']` query —
 * `AuthGateway` then flips logged-out → logged-in without a reload.
 *
 * BroadcastChannel is same-origin only, so any message on it is trusted; the
 * refetch is the authoritative check regardless. A closed popup is the "user
 * bailed" fallback that stops the watch.
 */

const POPUP_SIGNIN_URL = '/auth/signin/popup';
// A blocked popup (or a browser without BroadcastChannel) falls back to the
// plain full-page sign-in redirect so the button is never a dead end.
const FALLBACK_SIGNIN_URL = '/auth/signin';
const AUTH_CHANNEL = 'gs-auth';
const POPUP_NAME = 'gs-login';
const POPUP_WIDTH = 500;
const POPUP_HEIGHT = 650;
const POPUP_CLOSED_POLL_MS = 500;
// Stop watching an abandoned popup so the channel/poll can't linger.
const SAFETY_TIMEOUT_MS = 5 * 60 * 1000;

// Centre the popup over the opener window (handles multi-monitor offsets).
const buildPopupFeatures = () => {
  const screenLeft = window.screenLeft ?? window.screenX ?? 0;
  const screenTop = window.screenTop ?? window.screenY ?? 0;
  const viewportWidth = window.outerWidth || document.documentElement.clientWidth || POPUP_WIDTH;
  const viewportHeight = window.outerHeight || document.documentElement.clientHeight || POPUP_HEIGHT;
  const left = Math.round(screenLeft + Math.max(0, (viewportWidth - POPUP_WIDTH) / 2));
  const top = Math.round(screenTop + Math.max(0, (viewportHeight - POPUP_HEIGHT) / 2));

  return `popup=yes,width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top}`;
};

export const useLoginPopup = () => {
  const queryClient = useQueryClient();
  const popupRef = useRef<Window | null>(null);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopWatching = useCallback(() => {
    channelRef.current?.close();
    channelRef.current = null;

    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const handleSignedIn = useCallback(() => {
    stopWatching();
    popupRef.current?.close();
    popupRef.current = null;
    // Shared with AuthGateway — refetching the session flips it to the app.
    queryClient.invalidateQueries({ queryKey: ['auth', 'userinfo'] });
  }, [queryClient, stopWatching]);

  const openLoginPopup = useCallback(() => {
    // An already-open popup is focused rather than spawning a second one.
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.focus();

      return;
    }

    // Without BroadcastChannel we can't hear the completion signal, so a popup is
    // useless — go straight to the full-page redirect instead.
    if (typeof BroadcastChannel === 'undefined') {
      window.location.assign(FALLBACK_SIGNIN_URL);

      return;
    }

    // Opened synchronously from the click handler so it counts as a user gesture
    // and isn't blocked.
    const popup = window.open(POPUP_SIGNIN_URL, POPUP_NAME, buildPopupFeatures());

    if (!popup) {
      window.location.assign(FALLBACK_SIGNIN_URL);

      return;
    }

    popupRef.current = popup;
    stopWatching();

    const channel = new BroadcastChannel(AUTH_CHANNEL);
    channel.addEventListener('message', handleSignedIn);
    channelRef.current = channel;

    // Fallback: if the user closes the popup without finishing, stop watching.
    intervalRef.current = setInterval(() => {
      if (popupRef.current?.closed) {
        stopWatching();
      }
    }, POPUP_CLOSED_POLL_MS);

    timeoutRef.current = setTimeout(stopWatching, SAFETY_TIMEOUT_MS);
  }, [handleSignedIn, stopWatching]);

  useEffect(() => stopWatching, [stopWatching]);

  return openLoginPopup;
};
